import { useMemo } from 'react';
import {
  FilterGroupingEnum,
  FormAnswerMediaFilterInputObject,
  TaskCompletionFilterInputObject,
} from 'graphql-common';
import getBucketCreatedAtFilters from '@lib/components/TaskCompletions/utils/getBucketCreatedAtFilters';
import useInterventionLinkedCompletions from '@lib/components/TaskCompletions/utils/useInterventionLinkedCompletions';

// The completions an intervention covers — its "Completions: N".
export const getInterventionCompletionsFilters = (
  interventionId: string,
): TaskCompletionFilterInputObject[] => [
  {
    interventionId: {
      grouping: FilterGroupingEnum.And,
      predicate: { eq: interventionId },
    },
  },
];

type Args = {
  groupKey: string | null | undefined;
  // A timeline cell: only the completions of that bucket's period.
  bucketId: string | null;
  // An intervention: only the completions it covers. Wins over `bucketId`.
  interventionId?: string | null;
};

/**
 * Which completions (and which of their media) the modal lists. Media is
 * filtered through the answers carrying it, which know their completion but
 * not its intervention — so an intervention's scope is resolved to its
 * covered completion ids first, and `loading` holds the modal until then.
 */
export default function useTaskCompletionsScope({
  groupKey,
  bucketId,
  interventionId,
}: Args) {
  const { options: coveredCompletions, loading } =
    useInterventionLinkedCompletions({
      interventionId,
      taskGroupKey: groupKey,
      skip: !interventionId,
    });

  return useMemo(() => {
    if (interventionId) {
      const coveredIds = coveredCompletions.map(({ value }) => `${value}`);
      const formAnswerFilters: FormAnswerMediaFilterInputObject[] = [
        {
          taskCompletionId: {
            grouping: FilterGroupingEnum.And,
            // An empty `in` is dropped by the backend (matching everything);
            // `null` matches no answer, since every answer has a completion.
            predicate: coveredIds.length ? { in: coveredIds } : { null: true },
          },
        },
      ];
      return {
        completionFilters: getInterventionCompletionsFilters(interventionId),
        formAnswerFilters,
        loading,
      };
    }
    const bucketFilters = getBucketCreatedAtFilters(bucketId);
    return {
      completionFilters: bucketFilters as TaskCompletionFilterInputObject[],
      // Answers are written with their completion, so they share its
      // `createdAt` period.
      formAnswerFilters: bucketFilters as FormAnswerMediaFilterInputObject[],
      loading: false,
    };
  }, [interventionId, coveredCompletions, loading, bucketId]);
}
